import {Alert, Group, Stack, Text, Title} from '@mantine/core'

import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import {formatScheduleWindowLabel} from '../projects/project-schedules-label'
import {ServiceScheduleEditorPanel} from './service-schedule-editor-panel'
import {useServiceScheduleEditor} from './use-service-schedule-editor'

type ServiceSchedulePageProps = {
    projectId: string
    scheduleId: string
}

export function ServiceSchedulePage({projectId, scheduleId}: ServiceSchedulePageProps) {
    const editor = useServiceScheduleEditor({scheduleId})

    if (editor.isScheduleError) {
        return (
            <Stack gap="md">
                <Title order={1}>Schedule not found</Title>
                <Text c="dimmed">
                    This schedule may have been removed or does not belong to this order.
                </Text>
                <DetailPageBackLink
                    to="/projects/$projectId"
                    params={{projectId}}
                    listLabel="order"
                    backLabel="Back to order"
                />
            </Stack>
        )
    }

    if (editor.schedule != null && editor.schedule.projectId !== projectId) {
        return (
            <Stack gap="md">
                <Title order={1}>Schedule not found</Title>
                <Text c="dimmed">This schedule does not belong to the named order.</Text>
                <DetailPageBackLink
                    to="/projects/$projectId"
                    params={{projectId}}
                    listLabel="order"
                    backLabel="Back to order"
                />
            </Stack>
        )
    }

    const pageTitle =
        editor.schedule == null
            ? 'Service schedule'
            : formatScheduleWindowLabel(editor.schedule.startDate, editor.schedule.endDate)

    return (
        <Stack gap="lg">
            <Stack gap={4}>
                <DetailPageBackLink
                    to="/projects/$projectId"
                    params={{projectId}}
                    listLabel="order"
                    backLabel="Back to order"
                />
                <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                        <Title order={1}>{pageTitle}</Title>
                        <Text size="sm" c="dimmed">
                            Review changes before applying updates to this schedule.
                        </Text>
                    </Stack>
                </Group>
            </Stack>

            {editor.schedule == null && !editor.isLoading ? (
                <Alert color="red" title="Could not load schedule">
                    Failed to load this service schedule.
                </Alert>
            ) : (
                <ServiceScheduleEditorPanel
                    editor={editor}
                    patternDisabled
                    cadenceDisabled={false}
                />
            )}
        </Stack>
    )
}
